import React, {useMemo, useState, useEffect} from "react"
import {useAccessToken, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {getBillpayOrgSettings, updateBillpayOrgSettings, BillpayOrgSettings} from "../../resources/billpayOrgSettings"
import {getOrg, Org} from "../../resources/org"
import {OkDocument} from "../../resources/common"
import Loading from "../../components/Loading/Loading"
import {useParams} from "react-router"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import Card from "../../containers/Card/Card"
import {HorizontalField} from "../BillPayTerms/HorizontalField"
import Icon from "../../components/Icon/Icon"
import {useToasts} from "react-toast-notifications"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {v4 as uuidv4} from "uuid"
import {getOrgId} from "./utils"

function ReadOnlyTextField({label, value}: {label: string; value: string}) {
	return (
		<HorizontalField label={label}>
			<input className="input" type="text" value={value} disabled readOnly />
		</HorizontalField>
	)
}

function CheckboxField({
	label,
	checked,
	disabled,
	onChange,
}: {
	label: string
	checked: boolean
	disabled: boolean
	onChange: (value: boolean) => void
}) {
	return (
		<HorizontalField label={label}>
			<label className="checkbox">
				<input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />{" "}
				{checked ? "Enabled" : "Disabled"}
			</label>
		</HorizontalField>
	)
}

function Page({settings, org, refresh}: {settings: BillpayOrgSettings; org: Org; refresh: () => void}) {
	const orgId = getOrgId(settings)
	const isUnitUser = useIsUnitUser()
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const [isEditing, setIsEditing] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [enableCustomerLevelCategories, setEnableCustomerLevelCategories] = useState(
		settings.attributes.enableCustomerLevelCategories
	)
	const [autoLinkAccounts, setAutoLinkAccounts] = useState(settings.attributes.autoLinkAccounts)

	useEffect(() => {
		if (!isEditing) {
			setEnableCustomerLevelCategories(settings.attributes.enableCustomerLevelCategories)
			setAutoLinkAccounts(settings.attributes.autoLinkAccounts)
		}
	}, [settings, isEditing])

	const orgName = useMemo(() => org.attributes.name, [org])

	const handleSave = async () => {
		if (!orgId) return
		setIsSaving(true)

		try {
			const result = await updateBillpayOrgSettings(
				accessToken,
				orgId,
				enableCustomerLevelCategories,
				autoLinkAccounts,
				uuidv4()
			)

			result.match(
				() => {
					addToast("Bill Pay Org Settings updated successfully", {appearance: "success"})
					setIsEditing(false)
					refresh()
				},
				(error) => {
					addToast(error.errors?.[0]?.detail || error.errors?.[0]?.title || "Failed to update", {
						appearance: "error",
					})
				}
			)
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<>
			<TitleBar
				title={`#${settings.id}`}
				breadcrumbs={[{text: "Bill Pay Org Settings", path: "/admin?tab=Bill%20Pay%20Org%20Settings"}]}
				meta={orgId ? [{text: orgName, icon: "user-hierachy2" as const}] : undefined}
			>
				{isUnitUser && (
					<>
						{!isEditing ? (
							<button className="button page-title-button" onClick={() => setIsEditing(true)}>
								<Icon icon="pencil-1--interface-essential" size={16} />
								<span>Edit</span>
							</button>
						) : (
							<>
								<button className="button button-cancel" onClick={() => setIsEditing(false)} disabled={isSaving}>
									<Icon icon="interface-delete-interface-essential" size={12} />
									<span>Discard</span>
								</button>
								<button className="button page-title-button" onClick={handleSave} disabled={isSaving}>
									<span>{isSaving ? "Saving..." : "Save"}</span>
								</button>
							</>
						)}
					</>
				)}
			</TitleBar>

			<MainSection>
				<Card title="General">
					<ReadOnlyTextField label="ID" value={settings.id} />
					<ReadOnlyTextField label="Org" value={orgName} />
					<ReadOnlyTextField label="Banking Terms ID" value={settings.attributes.bankingTermsId} />
					<CheckboxField
						label="Enable Customer Level Categories"
						checked={isEditing ? enableCustomerLevelCategories : settings.attributes.enableCustomerLevelCategories}
						disabled={!isEditing}
						onChange={setEnableCustomerLevelCategories}
					/>
					<CheckboxField
						label="Auto Link Accounts"
						checked={isEditing ? autoLinkAccounts : settings.attributes.autoLinkAccounts}
						disabled={!isEditing}
						onChange={setAutoLinkAccounts}
					/>
				</Card>
			</MainSection>
		</>
	)
}

function PageWithOrg({settings, refresh}: {settings: OkDocument<BillpayOrgSettings>; refresh: () => void}) {
	const accessToken = useAccessToken()
	const orgId = settings.data.relationships.org.data.id
	const orgResult = useAsyncResult(() => getOrg(accessToken, orgId), [orgId])

	return orgResult.match(
		() => <Loading />,
		(org) => <Page settings={settings.data} org={org} refresh={refresh} />,
		() => null
	)
}

export default function BillPayOrgSettingsPage() {
	const {settingsId = ""} = useParams<{settingsId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()

	const settingsResult = useAsyncResult(
		() => getBillpayOrgSettings(accessToken, settingsId),
		[settingsId, refreshToken]
	)

	return settingsResult.match(
		() => <Loading />,
		(settings) => <PageWithOrg settings={settings} refresh={refresh} />,
		() => null
	)
}
